import { useState, useEffect } from "react";
import { useCookies } from "react-cookie";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import http from "../../../Common/http-common.js";
import * as common from "../../../Common/common.js";
import * as Comp from "../../../Common/CommonComponents.js";
import CssClass from "../../../Styles/common.module.css";

import ConfirmModal from "../../BasicElements/ConfirmModal.js";
import ExpensesAttachmentModal from "../../BasicElements/ExpensesAttachmentModal.js";
import ExpensesDetailsUpdate from "./ExpensesDetailsUpdate";
import ExpensesDetailsDelete from "./ExpensesDetailsDelete";

import { Modal } from "react-bootstrap";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSquarePlus, faFileImport, faPenToSquare, faTrashCan, faXmark, faFloppyDisk, faCheck } from "@fortawesome/free-solid-svg-icons";

import "primeicons/primeicons.css";
import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";

const PAGE_NAME = "ExpensesDraft.js_";
const MODULE_ID = "WEB_HIS_003";

const DRAFT_STATUS      = "2B0B24D1-3A4D-4FB6-B256-D92FA0304B0F";
const SUBMITTED_STATUS  = "2F917D27-2274-4509-9B9C-1A86FFFB6A45";
const APPROVED_STATUS   = "E29288AB-B30C-4039-9365-0324498F1352";
const CANCELLED_STATUS  = "64744C00-F6FA-49CF-B5F7-0146A24D7B9E";
const REJECTED_STATUS      = "6C2A9AE1-601B-4CA7-AF5D-7D96D95C4913";
const RESUBMITTED_STATUS   = "3F817D28-2265-8619-4B6C-1A86HHHB6A66";

const ExpensesDraft = () => {

    const navigate = useNavigate();
    const [cookies] = useCookies([]);
    const [searchParams] = useSearchParams();

    const userId = cookies.USER_ID;
    const userName = cookies.USERNAME;

    const expensesIdParam = searchParams.get("expensesId");
    const actionParam = searchParams.get("action") || "View";
    const isApproveMode  = actionParam === "Approve";
    const isResubmitMode = actionParam === "Resubmit";

    const [expensesId, setExpensesId] = useState("");
    const [allData, setAllData] = useState([]);
    const [status, setStatus] = useState("");

    const [showUpdate, setShowUpdate] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [updateData, setUpdateData] = useState(null);

    const [loading, setLoading] = useState(false);
    const [loadingText, setLoadingText] = useState("Loading...");
    const [inputError, setInputError] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedExpenses, setSelectedExpenses] = useState([]);

    // Approve mode state
    const [showApproveAllConfirm, setShowApproveAllConfirm] = useState(false);
    const [pendingApprovalList, setPendingApprovalList] = useState([]);

    // Resubmit mode state
    const [hasEdits, setHasEdits] = useState(false);
    const [showResubmitConfirm, setShowResubmitConfirm] = useState(false);

    // Back warning
    const [showBackWarning, setShowBackWarning] = useState(false);

    // Reject reason modal
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectModalRowId, setRejectModalRowId] = useState(null);
    const [rejectModalReason, setRejectModalReason] = useState('');

    const [categoryTypeList, setCategoryTypeList] = useState([]);

    const [editData, setEditData] = useState({
        DATE: "",
        CATEGORY_TYPE_MST_ID: "",
        CATEGORY_TYPE_NAME: "",
        AMOUNT: "",
        REQUIRED_RECEIPT: "",
        EXPENSES_DESC: "",
        REMARK: "",
        IS_MEDICAL: false,
        IS_MILEAGE: false,
        CALCULATED_AMOUNT: ""
    });

    // ATTACHMENT
    const [attachments, setAttachments] = useState([]);
    const [showAttachmentModal, setShowAttachmentModal] = useState(false);
    const [attachmentList, setAttachmentList] = useState([]);
    const [attachmentSource, setAttachmentSource] = useState("");
    const [attachmentMap, setAttachmentMap] = useState({});

    const openAttachmentModal = (id) => {
        setAttachmentSource(id);
        setShowAttachmentModal(true);
    };

    // MEDICAL CLAIM
    const [medicalInfo, setMedicalInfo] = useState({
        entitled: 0,
        claimed: 0,
        balance: 0
    });

    const totalAmount = allData.reduce((sum, row) => {
        const val = parseFloat(row.AMOUNT);
        return sum + (isNaN(val) ? 0 : val);
    }, 0);

    // LOAD TYPES
    useEffect(() => {
        http.get("api/expenses/GetExpensesType?_sysUser=" + userId)
            .then(res => {
                const arr = res.data.map(x => ({
                    key: x.CATEGORY_TYPE_MST_ID,
                    value: x.CATEGORY_TYPE_MST_ID,
                    label: x.CATEGORY_TYPE_NAME,
                    remark: x.REMARK,
                    receiptFlag: x.RECEIPT_FLAG,
                    mileageRate: x.MILEAGE_RATE ?? 0.5,
                }));
                setCategoryTypeList(arr);
            })
            .catch(err => {
                toast.error("Failed to get expenses type.");
                common.c_LogWebError(PAGE_NAME, "GetExpensesType", err);
            });
    }, []);

    // LOAD DRAFT DATA
    useEffect(() => {
        if (!expensesIdParam) return;

        setLoading(true);
        setLoadingText("Loading draft...");

        http.get(
            "api/expensesHistory/GetExpenses?_sysUser=" + userId +
            "&_expensesId=" + expensesIdParam
        )
            .then(res => {
                if (res.data?.length > 0) {
                    const formatted = res.data.map(d => ({
                        ...d,
                        AMOUNT: parseFloat(d.AMOUNT || 0).toFixed(2)
                    }));

                    // In approve mode, embed approval state fields directly in each row
                    // Pre-set _APPROVE_FLAG to 'Y' for already-approved lines so dropdown defaults to Approve
                    const dataToStore = isApproveMode
                        ? formatted.map(d => ({ ...d, _APPROVE_FLAG: d.APPROVE_FLAG === 'Y' ? 'Y' : '', _REMARK: d.REMARK || '' }))
                        : formatted;

                    setAllData(dataToStore);
                    setExpensesId(formatted[0].EXPENSES_MST_ID);
                    setStatus(formatted[0].STATUS);
                }
            })
            .catch(err => {
                toast.error("Failed to load draft.");
                common.c_LogWebError(PAGE_NAME, "LoadDraft", err);
            })
            .finally(() => {
                setLoading(false);
                setLoadingText("Loading...");
            });

    }, [expensesIdParam]);

    // LOAD ATTACHMENTS FOR EACH ROW
    useEffect(() => {
        const loadAll = async () => {
            for (let row of allData) {
                if (row.EXPENSES_DETAILS_ID && attachmentMap[row.EXPENSES_DETAILS_ID] === undefined) {
                    await loadAttachments(row.EXPENSES_DETAILS_ID);
                }
            }
        };

        loadAll();
    }, [allData]);

    // LOAD MEDICAL CLAIM
    useEffect(() => {
        const year = new Date().getFullYear();

        http.get(
            "api/home/GetUserLeaveEntitlement?_sysUser=" + userId + "&_year=" + year
        )
            .then(res => {
                if (res.data?.length > 0) {
                    const data = res.data[0];
                    setMedicalInfo({
                        entitled: data.MEDICAL_CLAIM_ENTITLED || 0,
                        claimed: data.MEDICAL_CLAIMED || 0,
                        balance: data.MEDICAL_CLAIM_BALANCE || 0
                    });
                }
            })
            .catch(err => {
                toast.error("Failed to load Medical Claim.");
                common.c_LogWebError(PAGE_NAME, "LoadMedicalClaim", err);
            });
    }, [userId]);

    const loadAttachments = async (detailsId) => {
        if (!detailsId) return;

        try {
            const res = await http.get("api/expenses/GetExpensesAttachment?source=" + detailsId);
            const count = res.data?.length || 0;
            setAttachmentMap(prev => ({ ...prev, [detailsId]: count }));
        } catch {
            setAttachmentMap(prev => ({ ...prev, [detailsId]: 0 }));
        }
    };

    // INPUT
    const inputHandler = (event) => {
        const id = event.target.id;
        const val = event.target.value;

        setEditData(prev => ({ ...prev, [id]: val }));

        if (val.length === 0 || val.trim().length === 0) {
            setInputError(prev => ({ ...prev, [id]: id + " could not be empty." }));
        } else {
            setInputError(prev => ({ ...prev, [id]: "" }));
        }
    };

    const selectTypeHandler = (val) => {
        const selected = categoryTypeList.find(x => x.value === val.value);
        const receiptFlag = selected?.receiptFlag?.toString().trim().toUpperCase() || "N";
        const isMedical = selected?.label?.toLowerCase().includes("medical");
        const isMileage = selected?.label?.toLowerCase().includes("mileage");

        setEditData(prev => ({
            ...prev,
            CATEGORY_TYPE_MST_ID: val.value,
            CATEGORY_TYPE_NAME: selected?.label || "",
            REQUIRED_RECEIPT: receiptFlag === "Y" ? "YES" : "NO",
            REMARK: selected?.remark === "-" ? "" : selected?.remark,
            IS_MEDICAL: isMedical,
            IS_MILEAGE: isMileage,
            MILEAGE_RATE: isMileage ? (selected?.mileageRate ?? 0.5) : 0,
        }));

        setInputError(prev => ({ ...prev, CATEGORY_TYPE_MST_ID: "" }));
    };

    // ADD
    const AddBtnOnClick = () => {
        let functionName = "AddExpenses";

        setLoading(true);
        setLoadingText("Adding Expenses...");

        const totalClaim = totalAmount + (
            editData.IS_MILEAGE
                ? parseFloat(editData.CALCULATED_AMOUNT || 0)
                : parseFloat(editData.AMOUNT || 0)
        );

        const data = {
            EXPENSES_MST_ID: expensesId,
            USER_MST_GUID: userId,
            TOTAL_CLAIM: totalClaim,
            UPDATE_ID: userId,
            UPDATE_NAME: userName,
            DATE: editData.DATE ? editData.DATE + "T00:00:00" : null,
            CATEGORY_TYPE_MST_ID: editData.CATEGORY_TYPE_MST_ID,
            CATEGORY_TYPE_NAME: editData.CATEGORY_TYPE_NAME,
            AMOUNT: editData.IS_MILEAGE ? editData.CALCULATED_AMOUNT : editData.AMOUNT,
            EXPENSES_DESC: editData.EXPENSES_DESC,
            REMARK: editData.REMARK,
            FROM_SOURCE: { SOURCE: "WEB", MODULE_ID }
        };

        http.post("api/expenses/AddExpenses", data)
            .then(async (response) => {
                const mstId = response.data.EXPENSES_MST_ID;
                const detailsId = response.data.EXPENSES_DETAILS_ID;

                setExpensesId(mstId);

                const newRowData = {
                    ...editData,
                    DATE: editData.DATE + "T00:00:00",
                    EXPENSES_DETAILS_ID: detailsId,
                    EXPENSES_MST_ID: mstId,
                    AMOUNT: editData.IS_MILEAGE ? editData.CALCULATED_AMOUNT : editData.AMOUNT
                };

                // Upload attachments first so the count is accurate when row appears
                for (let f of attachments) {
                    const formData = new FormData();
                    formData.append("file", f.file);
                    formData.append("EXPENSES_DETAILS_ID", detailsId);
                    formData.append("USER_ID", userId);

                    await http.post(
                        "api/expenses/UploadExpensesAttachment",
                        formData,
                        { headers: { "Content-Type": "multipart/form-data" } }
                    );
                }

                const uploadedCount = attachments.length;

                // Set attachmentMap before allData so useEffect sees it and skips re-fetch
                setAttachmentMap(prev => ({ ...prev, [detailsId]: uploadedCount }));
                setAllData(prev => [...prev, newRowData]);

                setEditData({
                    DATE: "",
                    CATEGORY_TYPE_MST_ID: "",
                    CATEGORY_TYPE_NAME: "",
                    AMOUNT: "",
                    REQUIRED_RECEIPT: "",
                    EXPENSES_DESC: "",
                    REMARK: "",
                    IS_MEDICAL: false,
                    IS_MILEAGE: false,
                    CALCULATED_AMOUNT: ""
                });

                setAttachments([]);
                setInputError({});
                toast.success("Expenses Added.");
            })
            .catch(err => {
                toast.error("Expenses Failed add.");
                common.c_LogWebError(PAGE_NAME, functionName, err);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleAdd = () => {
        let flag = true;

        if (!editData.DATE) {
            flag = false;
            setInputError(prev => ({ ...prev, DATE: "Date could not be empty." }));
        }

        if (!editData.CATEGORY_TYPE_MST_ID) {
            flag = false;
            setInputError(prev => ({ ...prev, CATEGORY_TYPE_MST_ID: "Type could not be empty." }));
        }

        if (!editData.AMOUNT || isNaN(editData.AMOUNT)) {
            flag = false;
            setInputError(prev => ({ ...prev, AMOUNT: "Please enter a valid number." }));
        }

        if (!editData.EXPENSES_DESC) {
            flag = false;
            setInputError(prev => ({ ...prev, EXPENSES_DESC: "Description could not be empty." }));
        }

        if (editData.IS_MEDICAL) {
            const entered = editData.IS_MILEAGE
                ? parseFloat(editData.CALCULATED_AMOUNT || 0)
                : parseFloat(editData.AMOUNT || 0);

            if (entered > medicalInfo.balance) {
                toast.error("Exceed medical claim balance!");
                return;
            }
        }

        if (flag) {
            if (editData.REQUIRED_RECEIPT === "YES" && attachments.length === 0) {
                toast.error("Attachment is required for this type.");
                return;
            }
            AddBtnOnClick();
        }
    };

    // SUBMIT
    const ApplyExpenses = () => {
        let functionName = "ApplyExpenses";

        setLoading(true);
        setLoadingText("Applying Expenses...");

        const data = {
            EXPENSES_MST_ID: expensesId,
            USER_MST_GUID: userId,
            TOTAL_CLAIM: totalAmount,
            UPDATE_ID: userId,
            UPDATE_NAME: userName,
            DETAILS: allData,
            FROM_SOURCE: { SOURCE: "WEB", MODULE_ID }
        };

        http.post("api/expenses/ApplyExpenses", data)
            .then(() => {
                toast.success("Expenses submitted successfully.");
                setExpensesId("");
                setAllData([]);
                setTimeout(() => navigate("/ExpensesHistory"), 800);
            })
            .catch(err => {
                toast.error(
                    err?.response?.data?.MESSAGE || "Failed to apply expenses."
                );
                common.c_LogWebError(PAGE_NAME, functionName, err);
            })
            .finally(() => {
                setLoading(false);
                setLoadingText("Loading...");
            });
    };

    const handleConfirm = () => {
        if (allData.length === 0) {
            toast.error("Table cannot be empty.");
            return;
        }

        const invalidRow = allData.find(
            row =>
                row.REQUIRED_RECEIPT === "YES" &&
                (!attachmentMap[row.EXPENSES_DETAILS_ID] || attachmentMap[row.EXPENSES_DETAILS_ID] === 0)
        );

        if (invalidRow) {
            toast.error("All required receipt items must have attachment.");
            return;
        }

        setShowConfirmModal(true);
    };

    const handleConfirmModal = async () => {
        try {
            ApplyExpenses();
        } catch (err) {
            toast.error("Upload failed.");
        }
        setShowConfirmModal(false);
    };

    const handleConfirmModalClose = () => {
        setShowConfirmModal(false);
    };

    // UPDATE / DELETE
    const showUpdateModal = (e, row) => {
        setShowUpdate(true);
        const matchedType = categoryTypeList.find(t => t.value === row.CATEGORY_TYPE_MST_ID);
        setUpdateData({
            ...row,
            MILEAGE_RATE: matchedType?.mileageRate || row.MILEAGE_RATE,
            IS_MILEAGE: !!(row.IS_MILEAGE || row.CATEGORY_TYPE_NAME?.toLowerCase().includes("mileage")),
            IS_MEDICAL: !!(row.IS_MEDICAL || row.CATEGORY_TYPE_NAME?.toLowerCase().includes("medical"))
        });
    };

    const hideUpdateModal = () => {
        setShowUpdate(false);
        setUpdateData(null);
    };

    const showDeleteModal = (row) => {
        setShowDelete(true);
        setUpdateData(row);
    };

    const hideDeleteModal = () => {
        setShowDelete(false);
        setUpdateData(null);
    };

    const onReloadHandler = (deletedRow) => {
        if (!deletedRow) return;

        setAllData(prev => {
            const updated = prev.filter(
                x => x.EXPENSES_DETAILS_ID !== deletedRow.EXPENSES_DETAILS_ID
            );

            if (updated.length === 0) {
                setExpensesId("");
                setAttachmentMap({});
            }

            return updated;
        });
    };

    const onLoadingHandler = (load, text) => {
        setLoading(load);
        setLoadingText(text);
    };

    const handleUpdateRow = (updatedRow) => {
        setAllData(prev =>
            prev.map(row =>
                row.EXPENSES_DETAILS_ID === updatedRow.EXPENSES_DETAILS_ID
                    ? updatedRow
                    : row
            )
        );
        if (isResubmitMode) setHasEdits(true);
    };

    // APPROVE MODE HANDLERS
    const handleSaveApproval = () => {
        const detailList = allData.map(d => ({
            EXPENSES_DETAILS_ID: d.EXPENSES_DETAILS_ID,
            APPROVE_FLAG: d._APPROVE_FLAG || '',
            REMARK: d._REMARK || ''
        }));

        const allActioned = detailList.every(d => d.APPROVE_FLAG === 'Y' || d.APPROVE_FLAG === 'N');
        if (!allActioned) {
            toast.warning("Please complete all items before saving.");
            return;
        }

        const missingRemark = detailList.find(d => d.APPROVE_FLAG === 'N' && !d.REMARK.trim());
        if (missingRemark) {
            toast.warning("Please provide a reason for all rejected items.");
            return;
        }

        const allApproved = detailList.every(d => d.APPROVE_FLAG === 'Y');
        if (allApproved) {
            setPendingApprovalList(detailList);
            setShowApproveAllConfirm(true);
        } else {
            callSaveDetailApproval(detailList);
        }
    };

    const callSaveDetailApproval = (detailList) => {
        setLoading(true);
        setLoadingText("Saving approval...");

        const data = {
            EXPENSES_MST_ID: expensesId,
            USER_MST_GUID: allData[0]?.USER_MST_GUID,
            TOTAL_CLAIM: totalAmount,
            UPDATE_ID: userId,
            UPDATE_NAME: userName,
            DETAIL_LIST: detailList,
            FROM_SOURCE: { SOURCE: "WEB", MODULE_ID }
        };

        http.post("api/expensesHistory/SaveDetailApproval", data)
            .then(res => {
                const msg = res.data.ALL_APPROVED
                    ? "Expenses approved successfully."
                    : "Some items rejected. Expenses has been updated.";
                toast.success(msg);
                setTimeout(() => navigate("/ExpensesHistory"), 800);
            })
            .catch(err => {
                toast.error(err?.response?.data?.MESSAGE || "Failed to save approval.");
                common.c_LogWebError(PAGE_NAME, "SaveDetailApproval", err);
            })
            .finally(() => {
                setLoading(false);
                setLoadingText("Loading...");
            });
    };

    // RESUBMIT HANDLER
    const handleResubmit = () => {
        setLoading(true);
        setLoadingText("Resubmitting expenses...");

        // Only resubmit rejected lines — approved lines remain unchanged
        const detailList = allData
            .filter(d => d.APPROVE_FLAG === 'N')
            .map(d => ({
                EXPENSES_DETAILS_ID: d.EXPENSES_DETAILS_ID,
                CATEGORY_TYPE_MST_ID: d.CATEGORY_TYPE_MST_ID,
                EXPENSES_DESC: d.EXPENSES_DESC,
                AMOUNT: d.AMOUNT,
                MILEAGE: d.MILEAGE || 0,
                DATE: d.DATE
            }));

        const data = {
            EXPENSES_MST_ID: expensesId,
            USER_MST_GUID: userId,
            UPDATE_ID: userId,
            UPDATE_NAME: userName,
            DETAIL_LIST: detailList,
            FROM_SOURCE: { SOURCE: "WEB", MODULE_ID }
        };

        http.post("api/expenses/ResubmitExpenses", data)
            .then(() => {
                setHasEdits(false);
                toast.success("Expenses resubmitted successfully.");
                setTimeout(() => navigate("/ExpensesHistory"), 800);
            })
            .catch(err => {
                toast.error(err?.response?.data?.MESSAGE || "Failed to resubmit expenses.");
                common.c_LogWebError(PAGE_NAME, "ResubmitExpenses", err);
            })
            .finally(() => {
                setLoading(false);
                setLoadingText("Loading...");
            });
    };

    // BACK WITH WARNING
    const handleBack = () => {
        if (isApproveMode || isResubmitMode) {
            setShowBackWarning(true);
        } else {
            navigate("/ExpensesHistory");
        }
    };

    // HEADER
    const renderHeader = () => (
        <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
            <div style={{ display: "flex", flexDirection: "column" }}>
                <span className="text-lg font-semibold" style={{ fontSize: "18px" }}>
                    Total Claimed
                </span>
                <span className="text-lg font-semibold" style={{ fontSize: "18px", fontWeight: "bold" }}>
                    RM {totalAmount === 0 ? "0" : totalAmount.toFixed(2)}
                </span>
            </div>

            <div
                style={{
                    width: "2px",
                    backgroundColor: "#ccc",
                    height: "3.2rem",
                    margin: "0 15px"
                }}
            />

            {status === SUBMITTED_STATUS ? (
                <Tag
                    icon="pi pi-check"
                    value="Submitted"
                    style={{ fontSize: "14px", padding: "0 0.6rem", height: "40px", display: "flex", alignItems: "center" }}
                />
            ) : status === APPROVED_STATUS ? (
                <Tag
                    icon="pi pi-check"
                    severity="success"
                    value="Approved"
                    style={{ fontSize: "14px", padding: "0 0.6rem", height: "40px", display: "flex", alignItems: "center" }}
                />
            ) : status === CANCELLED_STATUS ? (
                <Tag
                    icon="pi pi-times"
                    value="Cancelled"
                    style={{ fontSize: "14px", padding: "0 0.6rem", height: "40px", display: "flex", alignItems: "center", backgroundColor: "#9b59b6", color: "white" }}
                />
            ) : status === REJECTED_STATUS ? (
                <Tag
                    icon="pi pi-times"
                    severity="danger"
                    value="Rejected"
                    style={{ fontSize: "14px", padding: "0 0.6rem", height: "40px", display: "flex", alignItems: "center" }}
                />
            ) : status === RESUBMITTED_STATUS ? (
                <Tag
                    icon="pi pi-refresh"
                    value="Resubmitted"
                    style={{ fontSize: "14px", padding: "0 0.6rem", height: "40px", display: "flex", alignItems: "center", backgroundColor: "#e67e00", color: "white" }}
                />
            ) : (
                <Tag
                    icon="pi pi-exclamation-triangle"
                    severity="warning"
                    value="Pending"
                    style={{ fontSize: "14px", padding: "0 0.6rem", height: "40px", display: "flex", alignItems: "center" }}
                />
            )}
        </div>
    );

    // ATTACHMENT FILE HANDLERS
    const ALLOWED_TYPES = ["application/pdf", "image/jpeg", "image/jpg", "image/png", "image/webp", "image/avif", "image/tiff", "image/heic", "image/heif", "image/svg+xml"];

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        const validFiles = [];

        for (let f of files) {
            if (!ALLOWED_TYPES.includes(f.type)) {
                toast.error(`"${f.name}" is not allowed. Only PDF and image files are accepted.`);
                continue;
            }
            if (f.size > 4 * 1024 * 1024) {
                toast.error("Max file size is 4MB");
                continue;
            }
            validFiles.push({ file: f, name: f.name, size: f.size });
        }

        setAttachments(prev => [...prev, ...validFiles]);
    };

    const removeFile = (index) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    return (
        <>
            {/* TOP BUTTONS for non-Draft actions */}
            {actionParam !== "Draft" && status !== "" && (
                <div style={{ margin: "10px 20px 6px 20px", display: "flex", gap: "0.75rem", alignItems: "center" }}>
                    <Comp.Button id="btnBack" type="cancel" style={{ width: "120px", height: "38px" }} onClick={handleBack}>
                        <FontAwesomeIcon icon={faXmark} />&nbsp; BACK
                    </Comp.Button>

                    {isApproveMode && (
                        <Comp.Button id="btnSave" type="submit" style={{ width: "120px", height: "38px" }} onClick={handleSaveApproval}>
                            <FontAwesomeIcon icon={faFloppyDisk} />&nbsp; SUBMIT
                        </Comp.Button>
                    )}

                    {isResubmitMode && (
                        <Comp.Button id="btnResubmit" type="submit" style={{ width: "140px", height: "38px" }} onClick={() => {
                            if (!hasEdits) {
                                toast.warning("No changes detected. Please edit at least one item before resubmitting.");
                                return;
                            }
                            setShowResubmitConfirm(true);
                        }}>
                            <FontAwesomeIcon icon={faFileImport} />&nbsp; RESUBMIT
                        </Comp.Button>
                    )}
                </div>
            )}

            {/* ADD FORM - only shown when editing a draft (action=Draft) */}
            {actionParam === "Draft" && (
                <div className={`${CssClass.cardTwo} ${CssClass.tableCardTwo} allowanceTableWrapper`}>
                    <table className="allowanceTable">
                        <tr>
                            <td colSpan="3" style={{ paddingRight: "1rem" }}>
                                <Comp.Input
                                    id="DATE"
                                    label="Date*"
                                    type="date"
                                    value={editData.DATE}
                                    onChange={inputHandler}
                                    errorMessage={inputError.DATE}
                                />
                            </td>

                            <td colSpan="3" style={{ paddingRight: "1rem" }}>
                                <Comp.Select
                                    id="CATEGORY_TYPE_MST_ID"
                                    label="Type*"
                                    options={categoryTypeList}
                                    value={
                                        categoryTypeList.find(x => x.value === editData.CATEGORY_TYPE_MST_ID) || ""
                                    }
                                    onChange={selectTypeHandler}
                                    errorMessage={inputError.CATEGORY_TYPE_MST_ID}
                                />
                                {editData.IS_MEDICAL && (
                                    <div style={{ marginTop: "5px", fontSize: "13px", color: "#444" }}>
                                        Entitled: RM {medicalInfo.entitled.toFixed(2)} |&nbsp;
                                        Claimed: RM {medicalInfo.claimed.toFixed(2)} |&nbsp;
                                        Balance: <b>RM {medicalInfo.balance.toFixed(2)}</b>
                                    </div>
                                )}
                                {editData.IS_MILEAGE && (
                                    <div style={{ marginTop: "5px", fontSize: "13px", color: "#444" }}>
                                        Rate: <b>RM {parseFloat(editData.MILEAGE_RATE || 0.5).toFixed(2)} per KM</b>
                                    </div>
                                )}
                            </td>

                            <td colSpan="3" style={{ paddingRight: "1rem" }}>
                                <Comp.Input
                                    id="AMOUNT"
                                    label={editData.IS_MILEAGE ? "Distance (KM)*" : "Amount (RM)*"}
                                    value={editData.AMOUNT}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (!/^\d*\.?\d*$/.test(val)) return;

                                        if (editData.IS_MILEAGE) {
                                            const km = parseFloat(val || 0);
                                            const rate = parseFloat(editData.MILEAGE_RATE || 0.5);
                                            const rm = km * rate;
                                            setEditData(prev => ({
                                                ...prev,
                                                AMOUNT: val,
                                                CALCULATED_AMOUNT: rm.toFixed(2)
                                            }));
                                        } else {
                                            setEditData(prev => ({ ...prev, AMOUNT: val }));
                                        }
                                    }}
                                />
                            </td>

                            <td colSpan="3">
                                <label style={{ padding: "6px" }}>Required Receipt</label>
                                <input
                                    type="text"
                                    className="form-control-allowance"
                                    value={editData.REQUIRED_RECEIPT}
                                    readOnly
                                    style={{ backgroundColor: "#f2f2f2", color: "#666" }}
                                />
                            </td>
                        </tr>

                        <tr>
                            <td colSpan="6" style={{ paddingRight: "1rem" }}>
                                <label style={{ padding: "6px" }}>Description*</label>
                                <textarea
                                    id="EXPENSES_DESC"
                                    rows={2}
                                    className="form-control-allowance"
                                    value={editData.EXPENSES_DESC}
                                    onChange={inputHandler}
                                />
                                {inputError.EXPENSES_DESC && (
                                    <div className="text-danger mt-1">{inputError.EXPENSES_DESC}</div>
                                )}
                            </td>

                            <td colSpan="6">
                                <label style={{ padding: "6px" }}>Remark</label>
                                <textarea
                                    id="REMARK"
                                    rows={2}
                                    className="form-control-allowance"
                                    value={editData.REMARK}
                                    readOnly
                                    style={{ backgroundColor: "#f2f2f2", color: "#666" }}
                                />
                            </td>
                        </tr>

                        {/* ATTACHMENT */}
                        <tr>
                            <td colSpan="12" style={{ padding: "2rem 0" }}>
                                <div
                                    style={{
                                        display: "flex",
                                        border: "1px solid #ddd",
                                        borderRadius: "6px",
                                        overflow: "hidden",
                                        // width: "49%",
                                        width: "100%",
                                        minWidth: "500px"
                                    }}
                                >
                                    <div
                                        style={{
                                            width: "220px",
                                            background: "#f5f5f5",
                                            padding: "10px",
                                            borderRight: "1px solid #ddd",
                                            fontWeight: "500"
                                        }}
                                    >
                                        Upload File (PDF and Image file only)
                                        <div style={{ fontSize: "12px", color: "#555", marginTop: "4px" }}>
                                            (Max File Size 4MB per file)
                                        </div>
                                    </div>

                                    <div style={{ flex: 1 }}>
                                        <table style={{ width: "100%", fontSize: "1rem", borderCollapse: "collapse" }}>
                                            <thead style={{ background: "#f5f5f5" }}>
                                                <tr>
                                                    <th style={{ width: "40px", textAlign: "center", padding: "0 8px", height: "38px", verticalAlign: "middle", border: "1px solid #ddd" }}>#</th>
                                                    <th style={{ textAlign: "left", padding: "0 8px", verticalAlign: "middle", border: "1px solid #ddd" }}>Filename</th>
                                                    <th style={{ width: "100px", textAlign: "center", padding: "0 8px", verticalAlign: "middle", border: "1px solid #ddd" }}>Size</th>
                                                    <th style={{ width: "80px", textAlign: "center", padding: "0 8px", verticalAlign: "middle", border: "1px solid #ddd" }}>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {attachments.map((f, i) => (
                                                    <tr key={i} style={{ height: "38px" }}>
                                                        <td style={{ textAlign: "center", padding: "0 8px", verticalAlign: "middle", border: "1px solid #ddd" }}>{i + 1}</td>
                                                        <td style={{ padding: "0 8px", verticalAlign: "middle", border: "1px solid #ddd" }}>{f.name}</td>
                                                        <td style={{ textAlign: "center", padding: "0 8px", verticalAlign: "middle", border: "1px solid #ddd" }}>
                                                            {(f.size / 1024).toFixed(0)} KB
                                                        </td>
                                                        <td style={{ textAlign: "center", padding: "0 8px", verticalAlign: "middle", border: "1px solid #ddd" }}>
                                                            <span
                                                                style={{ color: "#d9534f", cursor: "pointer" }}
                                                                onClick={() => removeFile(i)}
                                                            >
                                                                DELETE
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                                <tr style={{ height: "38px" }}>
                                                    <td></td>
                                                    <td></td>
                                                    <td></td>
                                                    <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                                                        <label style={{ cursor: "pointer", fontWeight: "bold", fontSize: "20px" }}>
                                                            +
                                                            <input
                                                                type="file"
                                                                multiple
                                                                accept=".pdf,.jpg,.jpeg,.png,.webp,.avif,.tif,.tiff,.heic,.heif,.svg"
                                                                style={{ display: "none" }}
                                                                onChange={(e) => {
                                                                    handleFileSelect(e);
                                                                    e.target.value = null;
                                                                }}
                                                            />
                                                        </label>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </td>
                        </tr>

                        <tr>
                            <td colSpan="12" style={{ padding: "0.5rem 1rem 0.5rem 0" }}>
                                <div style={{ display: "flex", gap: "1rem" }}>
                                    <Comp.Button id="btnBack" type="cancel" style={{ width: "120px", height: "38px" }} onClick={handleBack}>
                                        <FontAwesomeIcon icon={faXmark} />&nbsp; BACK
                                    </Comp.Button>

                                    <Comp.Button id="btnAdd" type="submit" onClick={handleAdd}>
                                        <FontAwesomeIcon icon={faSquarePlus} /> ADD
                                    </Comp.Button>

                                    <Comp.Button id="btnSubmit" type="submit" onClick={handleConfirm}>
                                        Submit <FontAwesomeIcon icon={faFileImport} />
                                    </Comp.Button>
                                </div>
                            </td>
                        </tr>
                    </table>
                </div>
            )}

            {/* TABLE */}
            <div className={`${CssClass.cardTwo} ${CssClass.tableCardTwo}`}>
                <div style={{ width: "100%" }}>
                    <DataTable
                        key={Object.keys(attachmentMap).join(",")}
                        value={allData}
                        dataKey="EXPENSES_DETAILS_ID"
                        header={renderHeader()}
                        loading={loading}
                        removableSort
                        emptyMessage="Fill in and add to expenses claim."
                        first={currentPage * rowsPerPage}
                        paginator
                        rows={rowsPerPage}
                        onPage={(e) => { setCurrentPage(e.page); setRowsPerPage(e.rows); }}
                        rowsPerPageOptions={[10, 20, 30, 40, 50]}
                        selectionMode="multiple"
                        selection={selectedExpenses}
                        onSelectionChange={(e) => setSelectedExpenses(e.value)}
                        scrollable
                        scrollHeight="700px"
                        showGridlines
                        rowHover
                        rowStyle={(data) =>
                            isResubmitMode && data.APPROVE_FLAG === 'Y'
                                ? { opacity: 0.5, backgroundColor: "#f5f5f5", pointerEvents: "none" }
                                : {}
                        }
                        className="datatable"
                        tableStyle={{ minWidth: "90rem" }}
                    >
                        {/* Draft: edit + delete per row */}
                        {status === DRAFT_STATUS && (
                            <Column
                                header="Action"
                                body={(rowData) => (
                                    <div style={{ textAlign: "-webkit-center" }} className={CssClass.tdBtnActionWrapperTwo}>
                                        <span title="Modify">
                                            <Comp.Button id="btnEdit" type="general" onClick={(e) => showUpdateModal(e, rowData)}
                                                style={{ width: "50px", height: "35px", marginTop: "2px", marginBottom: "2px" }}>
                                                <FontAwesomeIcon icon={faPenToSquare} />
                                            </Comp.Button>
                                        </span>
                                        <span title="Delete">
                                            <Comp.Button id="btnDelete" type="delete" onClick={() => showDeleteModal(rowData)}
                                                style={{ width: "50px", height: "35px" }}>
                                                <FontAwesomeIcon icon={faTrashCan} />
                                            </Comp.Button>
                                        </span>
                                    </div>
                                )}
                                style={{ width: "90px" }}
                            />
                        )}

                        {/* Resubmit: edit only for rejected rows */}
                        {isResubmitMode && (
                            <Column
                                header="Action"
                                body={(rowData) => rowData.APPROVE_FLAG === 'N' ? (
                                    <div style={{ textAlign: "-webkit-center" }} className={CssClass.tdBtnActionWrapperTwo}>
                                        <span title="Edit">
                                            <Comp.Button id="btnEdit" type="general" onClick={(e) => showUpdateModal(e, rowData)}
                                                style={{ width: "50px", height: "35px" }}>
                                                <FontAwesomeIcon icon={faPenToSquare} />
                                            </Comp.Button>
                                        </span>
                                    </div>
                                ) : null}
                                style={{ width: "70px" }}
                            />
                        )}

                        <Column
                            header="#"
                            body={(rowData, options) => options.rowIndex + 1}
                            style={{ width: "2.5rem", textAlign: "center", height: "40px" }}
                        />

                        <Column
                            field="DATE"
                            header="Date"
                            style={{ textAlign: "center" }}
                            body={(rowData) => {
                                if (!rowData.DATE) return "";
                                const d = new Date(rowData.DATE);
                                if (isNaN(d)) return "";
                                const day = String(d.getDate()).padStart(2, "0");
                                const month = String(d.getMonth() + 1).padStart(2, "0");
                                const year = d.getFullYear();
                                return `${day}/${month}/${year}`;
                            }}
                        />

                        <Column
                            field="CATEGORY_TYPE_NAME"
                            header="Type"
                            style={{ textAlign: "center" }}
                        />

                        <Column
                            field="EXPENSES_DESC"
                            header="Description"
                            style={{ minWidth: "250px" }}
                        />

                        <Column
                            field="AMOUNT"
                            header="Amount (RM)"
                            style={{ textAlign: "right" }}
                            bodyStyle={{ backgroundColor: "#ebf5eb", fontWeight: "bold" }}
                            body={(row) => {
                                const amount = parseFloat(row.AMOUNT || 0);
                                return (
                                    <div style={{ textAlign: "right" }}>
                                        RM {amount.toFixed(2)}
                                    </div>
                                );
                            }}
                        />

                        <Column
                            header="Attachment"
                            style={{ textAlign: "center", width: "120px" }}
                            headerStyle={{ textAlign: "center" }}
                            body={(row) => {
                                const count = attachmentMap[row.EXPENSES_DETAILS_ID];
                                if (count === undefined) return "-";
                                if (count === 0) return "-";
                                return (
                                    <i
                                        className="pi pi-file"
                                        style={{ cursor: "pointer", fontSize: "16px" }}
                                        onClick={() => openAttachmentModal(row.EXPENSES_DETAILS_ID)}
                                    />
                                );
                            }}
                        />

                        {/* Show approval status + reject reason when viewing rejected expense (not in approve mode) */}
                        {(status === REJECTED_STATUS && !isApproveMode) && (
                            <Column
                                header="Approval Status"
                                style={{ minWidth: "140px" }}
                                bodyStyle={{ padding: 0 }}
                                body={(row) => {
                                    const approved = row.APPROVE_FLAG === 'Y';
                                    return (
                                        <div style={{
                                            padding: "0.75rem 1rem",
                                            backgroundColor: approved ? "#ebf5eb" : "#fff5f5",
                                            color: approved ? "#000" : "#c00000",
                                            fontWeight: "600",
                                            height: "100%",
                                        }}>
                                            {approved ? "Approved" : "Rejected"}
                                        </div>
                                    );
                                }}
                            />
                        )}

                        {(status === REJECTED_STATUS && !isApproveMode) && (
                            <Column
                                field="REMARK"
                                header="Reject Reason"
                                style={{ minWidth: "200px" }}
                                bodyStyle={{ padding: 0 }}
                                body={(row) => (
                                    <div style={{
                                        padding: "0.75rem 1rem",
                                        backgroundColor: "#fff5f5",
                                        color: "#c00000",
                                        height: "100%",
                                    }}>
                                        {row.REMARK || '-'}
                                    </div>
                                )}
                            />
                        )}

                        {/* Approve: all rows show dropdown; previously-approved rows are pre-set to Approve */}
                        {isApproveMode && (
                            <Column
                                header="Approval Status"
                                style={{ minWidth: "160px" }}
                                body={(rowData) => (
                                    <select
                                        value={rowData._APPROVE_FLAG || ''}
                                        onChange={e => {
                                            const val = e.target.value;
                                            if (val === 'N') {
                                                setAllData(prev => prev.map(row =>
                                                    row.EXPENSES_DETAILS_ID === rowData.EXPENSES_DETAILS_ID
                                                        ? { ...row, _APPROVE_FLAG: 'N', _REMARK: '' }
                                                        : row
                                                ));
                                                setRejectModalRowId(rowData.EXPENSES_DETAILS_ID);
                                                setRejectModalReason(rowData._REMARK || '');
                                                setShowRejectModal(true);
                                            } else {
                                                setAllData(prev => prev.map(row =>
                                                    row.EXPENSES_DETAILS_ID === rowData.EXPENSES_DETAILS_ID
                                                        ? { ...row, _APPROVE_FLAG: val, _REMARK: '' }
                                                        : row
                                                ));
                                            }
                                        }}
                                        style={{ padding: "4px 6px", borderRadius: "4px", border: "1px solid #ccc", fontSize: "13px", appearance: "auto", WebkitAppearance: "auto", MozAppearance: "auto", width: "100%" }}
                                    >
                                        <option value="">-- Select --</option>
                                        <option value="Y">Approve</option>
                                        <option value="N">Reject</option>
                                    </select>
                                )}
                            />
                        )}

                        {/* Approve: Remark column showing entered reject reason */}
                        {isApproveMode && (
                            <Column
                                header="Remark"
                                style={{ minWidth: "200px" }}
                                body={(rowData) => {
                                    const remark = rowData._REMARK || '';
                                    if (!remark) return '-';
                                    return (
                                        <div style={{ color: "#c00000", backgroundColor: "#fff5f5", padding: "4px 6px", borderRadius: "4px" }}>
                                            {remark}
                                        </div>
                                    );
                                }}
                            />
                        )}
                    </DataTable>
                </div>
            </div>

            {/* UPDATE */}
            <ExpensesDetailsUpdate
                module={MODULE_ID}
                show={showUpdate}
                editData={updateData}
                onHide={hideUpdateModal}
                onReload={onReloadHandler}
                onLoading={onLoadingHandler}
                onUpdateRow={handleUpdateRow}
                medicalInfo={medicalInfo}
            />

            {/* CONFIRM */}
            <ConfirmModal
                show={showConfirmModal}
                onHide={handleConfirmModalClose}
                onConfirm={handleConfirmModal}
                prop="Confirm to apply"
            >
                Are you sure you want to apply this expenses?
            </ConfirmModal>

            {/* DELETE */}
            {showDelete && (
                <ExpensesDetailsDelete
                    page={PAGE_NAME}
                    module={MODULE_ID}
                    showHide={showDelete}
                    editData={updateData}
                    onHide={hideDeleteModal}
                    onReload={onReloadHandler}
                    onLoading={onLoadingHandler}
                />
            )}

            {/* ATTACHMENT MODAL */}
            <ExpensesAttachmentModal
                show={showAttachmentModal}
                onHide={() => setShowAttachmentModal(false)}
                source={attachmentSource}
                userId={userId}
                readOnly={status !== DRAFT_STATUS && !isResubmitMode}
                onChanged={() => { if (isResubmitMode) setHasEdits(true); }}
            />

            {/* Reject reason modal */}
            <Modal show={showRejectModal} onHide={() => {
                // Cancel: revert the dropdown back to empty
                setAllData(prev => prev.map(row =>
                    row.EXPENSES_DETAILS_ID === rejectModalRowId
                        ? { ...row, _APPROVE_FLAG: '', _REMARK: '' }
                        : row
                ));
                setShowRejectModal(false);
                setRejectModalRowId(null);
                setRejectModalReason('');
            }} centered>
                <Modal.Header>
                    <div style={{ backgroundColor: "#FF8C00", width: "100%" }}>
                        <Modal.Title style={{ textAlign: "left", marginLeft: "5px" }}>Reject Reason</Modal.Title>
                    </div>
                </Modal.Header>
                <Modal.Body>
                    <Comp.Input
                        id="rejectReason"
                        label="Reason*"
                        name="Reject reason"
                        type="text"
                        value={rejectModalReason}
                        onChange={e => setRejectModalReason(e.target.value)}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <div className="onright-container">
                        <Comp.Button id="btnCancelReject" type="cancel" onClick={() => {
                            setAllData(prev => prev.map(row =>
                                row.EXPENSES_DETAILS_ID === rejectModalRowId
                                    ? { ...row, _APPROVE_FLAG: '', _REMARK: '' }
                                    : row
                            ));
                            setShowRejectModal(false);
                            setRejectModalRowId(null);
                            setRejectModalReason('');
                        }}>
                            <FontAwesomeIcon icon={faXmark} />&nbsp; Cancel
                        </Comp.Button>
                        <Comp.Button id="btnConfirmReject" onClick={() => {
                            if (!rejectModalReason.trim()) {
                                toast.warning("Please enter a reject reason.");
                                return;
                            }
                            setAllData(prev => prev.map(row =>
                                row.EXPENSES_DETAILS_ID === rejectModalRowId
                                    ? { ...row, _APPROVE_FLAG: 'N', _REMARK: rejectModalReason.trim() }
                                    : row
                            ));
                            setShowRejectModal(false);
                            setRejectModalRowId(null);
                            setRejectModalReason('');
                        }}>
                            <FontAwesomeIcon icon={faCheck} />&nbsp; Reject
                        </Comp.Button>
                    </div>
                </Modal.Footer>
            </Modal>

            {/* Confirm: All items approved */}
            <ConfirmModal
                show={showApproveAllConfirm}
                onHide={() => setShowApproveAllConfirm(false)}
                onConfirm={() => {
                    setShowApproveAllConfirm(false);
                    callSaveDetailApproval(pendingApprovalList);
                }}
                prop="All Items Approved"
            >
                All items approved. Update claim status to APPROVED?
            </ConfirmModal>

            {/* Confirm: Resubmit */}
            <ConfirmModal
                show={showResubmitConfirm}
                onHide={() => setShowResubmitConfirm(false)}
                onConfirm={() => {
                    setShowResubmitConfirm(false);
                    handleResubmit();
                }}
                prop="Confirm Resubmit"
            >
                Are you sure you want to resubmit this expenses claim?
            </ConfirmModal>

            {/* Warning: Back */}
            <ConfirmModal
                show={showBackWarning}
                onHide={() => setShowBackWarning(false)}
                onConfirm={() => {
                    setShowBackWarning(false);
                    navigate("/ExpensesHistory");
                }}
                prop="Leave Page"
                yesLabel="Confirm"
                noLabel="Cancel"
            >
                Please ensure that all data is submitted before leaving this page.
            </ConfirmModal>

            {loading && <Comp.Loading>{loadingText}</Comp.Loading>}
            <Comp.AlertPopup />
        </>
    );
};

export default ExpensesDraft;